import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Search, QrCode, Ticket, CheckCircle2, Users, RotateCcw, Download, FileDown, BellRing, Hourglass, Phone, Contact, Loader2, HandCoins, Undo2, History, Wallet, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { DashboardLayout } from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Skeleton } from "../components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { api, formatDateTime, formatTime, errorMessage } from "../lib/api";
import { useLang } from "../context/LanguageContext";

// Vipps-hendingar (ePayment event log + webhooks) → lesbare namn
const VIPPS_EVENT_LABELS = {
  CREATED: "Opprettet",
  AUTHORIZED: "Godkjent av kjøper",
  CAPTURED: "Belastet",
  CANCELLED: "Kansellert",
  ABORTED: "Avbrutt av kjøper",
  EXPIRED: "Utløpt",
  TERMINATED: "Avsluttet uten betaling",
  REFUNDED: "Refundert",
};

const ORDER_STATUS_LABELS = {
  paid: "Betalt",
  pay_at_door: "Betal ved inngangen",
  pending_payment: "Venter på betaling",
  processing: "Behandles",
  cancelled: "Kansellert",
  refunding: "Refunderes …",
  refunded: "Refundert",
};

const kr = (ore) => (ore === null || ore === undefined ? "–" : `${Math.round(ore / 100)} kr`);

export default function AttendeesPage() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const [downloadingGuide, setDownloadingGuide] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sendingReminder, setSendingReminder] = useState(false);
  const [waitlist, setWaitlist] = useState({ count: 0, entries: [] });
  const [buyersData, setBuyersData] = useState({ buyers: [], total_buyers: 0, total_tickets: 0 });
  const [buyersSearch, setBuyersSearch] = useState("");
  const [buyersVersion, setBuyersVersion] = useState(0);
  const [refundingId, setRefundingId] = useState(null);
  const [logOrderId, setLogOrderId] = useState(null);
  const [logData, setLogData] = useState(null);
  const [logLoading, setLogLoading] = useState(false);
  const [refundReason, setRefundReason] = useState("");

  useEffect(() => {
    api
      .get(`/my/events/${id}/waitlist`)
      .then((res) => setWaitlist(res.data))
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    const timer = setTimeout(() => {
      api
        .get(`/my/events/${id}/buyers`, { params: { search: buyersSearch.trim() || undefined } })
        .then((res) => setBuyersData(res.data))
        .catch(() => {});
    }, 250);
    return () => clearTimeout(timer);
  }, [id, buyersSearch, buyersVersion]);

  const fetchData = useCallback(
    (q) => {
      api
        .get(`/my/events/${id}/attendees`, { params: { search: q || undefined } })
        .then((res) => setData(res.data))
        .catch((err) => toast.error(errorMessage(err)))
        .finally(() => setLoading(false));
    },
    [id]
  );

  useEffect(() => {
    const timer = setTimeout(() => fetchData(search), 250);
    return () => clearTimeout(timer);
  }, [search, fetchData]);

  const undoCheckin = async (code) => {
    try {
      await api.post(`/my/events/${id}/undo-checkin`, { code });
      toast.success(t("Innsjekk angret"));
      fetchData(search);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  // Refusjon: heile Vipps-ordren, frå arrangøren si eiga Vipps-salseining tilbake til kjøparen
  const refundOrder = async (orderId) => {
    setRefundingId(orderId);
    try {
      const { data: res } = await api.post(`/my/events/${id}/orders/${orderId}/refund`, { reason: refundReason.trim() });
      setRefundReason("");
      toast.success(t("Bestillingen er refundert ({n} kr)", { n: res.amount }), {
        description: t("Billettene er ugyldige og plassene er ledige igjen. Kjøperen får e-post om refusjonen."),
      });
      setBuyersVersion((v) => v + 1);
      fetchData(search);
    } catch (err) {
      toast.error(errorMessage(err, t("Kunne ikke refundere bestillingen")));
      setBuyersVersion((v) => v + 1);
    } finally {
      setRefundingId(null);
    }
  };

  const openPaymentLog = async (orderId) => {
    setLogOrderId(orderId);
    setLogData(null);
    setLogLoading(true);
    try {
      const { data: res } = await api.get(`/my/events/${id}/orders/${orderId}/payment-events`);
      setLogData(res);
    } catch (err) {
      toast.error(errorMessage(err, t("Kunne ikke hente betalingsloggen")));
      setLogOrderId(null);
    } finally {
      setLogLoading(false);
    }
  };

  const paymentLogRows = (() => {
    if (!logData) return [];
    const rows = [];
    for (const e of logData.vipps_events || []) {
      rows.push({ key: `v-${e.name}-${e.timestamp}-${e.pspReference || ""}`, time: e.timestamp, name: e.name, amount: e.amount?.value, source: "vipps", success: e.success !== false, detail: "" });
    }
    for (const e of logData.webhook_events || []) {
      rows.push({ key: `w-${e.event_key}`, time: e.timestamp || e.received_at, name: e.name, amount: e.amount_ore, source: "webhook", success: e.success !== false, detail: e.result || "" });
    }
    return rows.sort((a, b) => String(a.time || "").localeCompare(String(b.time || "")));
  })();

  const exportCsv = async () => {
    try {
      const res = await api.get(`/my/events/${id}/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "deltakerliste.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("Deltakerlisten er lastet ned"));
    } catch (err) {
      toast.error(errorMessage(err, t("Kunne ikke eksportere CSV")));
    }
  };

  const exportBuyersCsv = async () => {
    try {
      const res = await api.get(`/my/events/${id}/buyers-export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "kjoperoversikt.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("Kjøperoversikten er lastet ned"));
    } catch (err) {
      toast.error(errorMessage(err, t("Kunne ikke eksportere kjøperoversikt")));
    }
  };

  const [sendingDoorReminder, setSendingDoorReminder] = useState(false);
  const sendDoorReminders = async () => {
    setSendingDoorReminder(true);
    try {
      const { data: res } = await api.post(`/my/events/${id}/door-reminders`);
      toast.success(
        res.sent === 1 ? t("Påminnelse om betaling sendt til 1 bestilling") : t("Påminnelse om betaling sendt til {n} bestillinger", { n: res.sent }),
        res.sent < res.total ? { description: t("{n} kunne ikke sendes – se e-postloggen", { n: res.total - res.sent }) } : undefined
      );
    } catch (err) {
      toast.error(errorMessage(err, t("Kunne ikke sende påminnelse")));
    } finally {
      setSendingDoorReminder(false);
    }
  };

  const sendReminders = async () => {
    setSendingReminder(true);
    try {
      const { data: res } = await api.post(`/my/events/${id}/send-reminders`);
      toast.success(res.sent === 1 ? t("Påminnelse sendt til 1 bestilling") : t("Påminnelse sendt til {n} bestillinger", { n: res.sent }));
    } catch (err) {
      toast.error(errorMessage(err, t("Kunne ikke sende påminnelse")));
    } finally {
      setSendingReminder(false);
    }
  };

  const downloadInstructions = async () => {
    setDownloadingGuide(true);
    try {
      const res = await api.get(`/my/events/${id}/checkin-instructions.pdf`, { params: { lang }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = lang === "nn" ? "rightin-innsjekk-instruks-nn.pdf" : "rightin-innsjekk-instruks.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("Instruksen er lastet ned"));
    } catch (err) {
      toast.error(errorMessage(err, t("Kunne ikke lage PDF")));
    } finally {
      setDownloadingGuide(false);
    }
  };

  const exportDoorList = async () => {
    try {
      const res = await api.get(`/my/events/${id}/door-list-pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "betal-ved-inngangen.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("Listen «Betal ved inngangen» er lastet ned"));
    } catch (err) {
      toast.error(errorMessage(err, t("Kunne ikke lage PDF")));
    }
  };

  const exportPdf = async () => {
    try {
      const res = await api.get(`/my/events/${id}/attendees-pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "deltakerliste.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("Deltakerliste-PDF er lastet ned"));
    } catch (err) {
      toast.error(errorMessage(err, t("Kunne ikke lage PDF")));
    }
  };

  if (loading && !data) {
    return (
      <DashboardLayout>
        <div className="space-y-4">
          <Skeleton className="h-10 w-1/2" />
          <Skeleton className="h-32 rounded-2xl" />
          <Skeleton className="h-64 rounded-2xl" />
        </div>
      </DashboardLayout>
    );
  }

  if (!data) return null;
  const { event, stats, tickets } = data;
  const pct = stats.capacity > 0 ? Math.round((stats.checked_in / Math.max(1, stats.issued)) * 100) : 0;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors duration-150 mb-4" data-testid="attendees-back-link">
            <ArrowLeft className="w-4 h-4" />
            {t("Tilbake til oversikten")}
          </Link>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" data-testid="attendees-title">{event.title}</h1>
              <p className="text-muted-foreground mt-1">{formatDateTime(event.date)} · {[event.venue, event.city].filter(Boolean).join(", ")}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" onClick={sendReminders} disabled={sendingReminder} data-testid="attendees-send-reminder-button">
                <BellRing className="w-4 h-4 mr-1.5" />
                {sendingReminder ? t("Sender...") : t("Send påminnelse")}
              </Button>
              <Button variant="secondary" onClick={exportPdf} data-testid="attendees-export-pdf-button">
                <FileDown className="w-4 h-4 mr-1.5" />
                {t("Last ned PDF")}
              </Button>
              <Button variant="secondary" onClick={exportCsv} data-testid="attendees-export-csv-button">
                <Download className="w-4 h-4 mr-1.5" />
                {t("Eksporter CSV")}
              </Button>
              {(stats.door_unpaid > 0 || event.pay_at_door) && (
                <Button variant="secondary" onClick={exportDoorList} data-testid="attendees-door-list-button">
                  <HandCoins className="w-4 h-4 mr-1.5" />
                  {t("Betal ved inngangen (PDF)")}
                </Button>
              )}
              <Button asChild data-testid="attendees-checkin-button">
                <Link to={`/dashboard/sjekk-inn/${id}`}>
                  <QrCode className="w-4 h-4 mr-1.5" />
                  {t("Åpne innsjekk")}
                </Link>
              </Button>
              <Button variant="outline" size="sm" onClick={downloadInstructions} disabled={downloadingGuide} data-testid="attendees-instructions-button">
                {downloadingGuide ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileDown className="w-4 h-4 mr-1.5" />}
                {t("Instruks til døren (PDF)")}
              </Button>
            </div>
          </div>
        </div>

        <div className={`grid grid-cols-1 sm:grid-cols-3 ${stats.door_unpaid > 0 ? "lg:grid-cols-4" : ""} gap-4`}>
          {stats.door_unpaid > 0 && (
            <Card className="border-[hsl(var(--warning))]/60 bg-[hsl(var(--warning))]/10" data-testid="attendees-door-card">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
                  <HandCoins className="w-4 h-4" /> {t("Betal ved inngangen")}
                </div>
                <p className="text-2xl sm:text-3xl font-semibold tabular-nums" data-testid="attendees-stat-door">{stats.door_unpaid}</p>
                <p className="text-xs text-muted-foreground mt-1">{t("billetter ikke betalt – {n} kr å ta imot i døren", { n: stats.door_amount })}</p>
                {stats.door_unpaid > 0 && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="outline" className="mt-3 bg-background/70" disabled={sendingDoorReminder} data-testid="attendees-door-reminder-button">
                        {sendingDoorReminder ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <BellRing className="w-4 h-4 mr-1.5" />}
                        {t("Send betalingspåminnelse")}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent data-testid="attendees-door-reminder-confirm">
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t("Send påminnelse til alle som betaler ved inngangen?")}</AlertDialogTitle>
                        <AlertDialogDescription>
                          {t("Hver bestilling får en e-post med beløpet som skal betales, QR-kodene rett i e-posten og billettene som PDF. Dagen før arrangementet sendes denne påminnelsen automatisk.")}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid="attendees-door-reminder-cancel">{t("Avbryt")}</AlertDialogCancel>
                        <AlertDialogAction onClick={sendDoorReminders} data-testid="attendees-door-reminder-send">{t("Send nå")}</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </CardContent>
            </Card>
          )}
          <Card className="border-border">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
                <Ticket className="w-4 h-4" /> {t("Utstedt")}
              </div>
              <p className="text-2xl sm:text-3xl font-semibold tabular-nums" data-testid="attendees-stat-issued">{stats.issued}</p>
              <p className="text-xs text-muted-foreground mt-1">{t("av {n} kapasitet", { n: stats.capacity })}</p>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
                <CheckCircle2 className="w-4 h-4" /> {t("Sjekket inn")}
              </div>
              <p className="text-2xl sm:text-3xl font-semibold tabular-nums" data-testid="attendees-stat-checkedin">{stats.checked_in}</p>
              <div className="mt-2">
                <Progress value={pct} className="h-1.5" />
                <p className="text-xs text-muted-foreground mt-1">{t("{pct}% av utstedte", { pct })}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
                <Users className="w-4 h-4" /> {t("Ledige plasser")}
              </div>
              <p className="text-2xl sm:text-3xl font-semibold tabular-nums" data-testid="attendees-stat-remaining">{stats.remaining}</p>
            </CardContent>
          </Card>
        </div>

        <Card className="border-border" data-testid="buyers-card">
          <CardContent className="p-0">
            <div className="p-4 border-b border-border flex flex-wrap items-center gap-2">
              <Contact className="w-4 h-4 text-muted-foreground" />
              <h2 className="font-heading font-semibold" data-testid="buyers-heading">{t("Kjøpere ({n})", { n: buyersData.total_buyers })}</h2>
              <span className="text-xs text-muted-foreground">{buyersData.total_tickets === 1 ? t("1 billett totalt") : t("{n} billetter totalt", { n: buyersData.total_tickets })}</span>
              {buyersData.total_buyers > 0 && (
                <Button variant="secondary" size="sm" className="ml-auto" onClick={exportBuyersCsv} data-testid="buyers-export-csv-button">
                  <Download className="w-3.5 h-3.5 mr-1.5" />
                  {t("Eksporter CSV")}
                </Button>
              )}
            </div>
            <div className="p-4 border-b border-border">
              <div className="relative max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={buyersSearch}
                  onChange={(e) => setBuyersSearch(e.target.value)}
                  placeholder={t("Søk på navn, e-post eller telefon...")}
                  className="pl-9"
                  data-testid="buyers-search-input"
                />
              </div>
            </div>
            {buyersData.buyers.length === 0 ? (
              <div className="p-10 text-center" data-testid="buyers-empty-state">
                <Contact className="w-9 h-9 mx-auto text-muted-foreground mb-3" />
                <h3 className="font-heading font-semibold">{buyersSearch ? t("Ingen treff") : t("Ingen kjøpere ennå")}</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {buyersSearch ? t("Prøv et annet søk.") : t("Når noen bestiller billetter, ser du navn, e-post, telefon og antall her.")}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table data-testid="buyers-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("Navn")}</TableHead>
                      <TableHead>{t("E-post")}</TableHead>
                      <TableHead>{t("Telefon")}</TableHead>
                      <TableHead className="text-right">{t("Antall billetter")}</TableHead>
                      <TableHead>{t("Betaling")}</TableHead>
                      <TableHead>{t("Kjøpt")}</TableHead>
                      <TableHead className="w-40"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {buyersData.buyers.map((b) => (
                      <TableRow key={b.order_id} data-testid={`buyer-row-${b.order_id}`} className={b.gdpr_deleted || b.status === "refunded" ? "opacity-70" : undefined}>
                        <TableCell className="text-sm">
                          {b.gdpr_deleted ? (
                            <Badge variant="secondary" className="font-normal" data-testid={`buyer-gdpr-deleted-badge-${b.order_id}`}>
                              {t("Slettet (GDPR)")}
                            </Badge>
                          ) : (
                            <span className="inline-flex items-center gap-2 flex-wrap">
                              {b.name || <span className="text-muted-foreground">–</span>}
                              {b.gdpr_delete_requested && (
                                <Badge variant="outline" className="font-normal text-muted-foreground" data-testid={`buyer-gdpr-requested-badge-${b.order_id}`}>
                                  {t("Sletting forespurt (GDPR)")}
                                </Badge>
                              )}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {b.gdpr_deleted ? <span title={t("Kjøperen har bedt om GDPR-sletting")}>–</span> : b.email}
                        </TableCell>
                        <TableCell className="text-sm">
                          {b.phone ? (
                            <span className="inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-muted-foreground" />{b.phone}</span>
                          ) : (
                            <span className="text-muted-foreground">–</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-right tabular-nums font-medium">{b.quantity}</TableCell>
                        <TableCell className="text-sm">
                          {b.status === "refunded" ? (
                            <Badge variant="secondary" className="font-normal whitespace-nowrap" data-testid={`buyer-refunded-badge-${b.order_id}`}>
                              <Undo2 className="w-3 h-3 mr-1" />{t("Refundert")} · {b.refund_amount ?? b.total_amount} kr
                            </Badge>
                          ) : b.status === "refunding" ? (
                            <Badge variant="outline" className="font-normal whitespace-nowrap" data-testid={`buyer-refunding-badge-${b.order_id}`}>
                              <Loader2 className="w-3 h-3 mr-1 animate-spin" />{t("Refunderes …")}
                            </Badge>
                          ) : b.payment === "door" ? (
                            <Badge className="bg-[hsl(var(--warning))]/20 text-foreground border border-[hsl(var(--warning))]/60 hover:bg-[hsl(var(--warning))]/20 font-normal whitespace-nowrap" data-testid={`buyer-door-badge-${b.order_id}`}>
                              <HandCoins className="w-3 h-3 mr-1" />{t("Betal ved inngangen")} · {b.total_amount} kr
                            </Badge>
                          ) : b.payment === "vipps" ? (
                            <Badge className="bg-[hsl(var(--success))]/15 text-foreground border border-[hsl(var(--success))]/50 hover:bg-[hsl(var(--success))]/15 font-normal whitespace-nowrap" data-testid={`buyer-vipps-badge-${b.order_id}`}>
                              <Wallet className="w-3 h-3 mr-1" />Vipps · {b.total_amount} kr
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="font-normal" data-testid={`buyer-free-badge-${b.order_id}`}>{t("Gratis")}</Badge>
                          )}
                          {b.status === "refunded" && b.refund_reason && (
                            <p className="text-xs text-muted-foreground mt-1 max-w-[260px] truncate" title={b.refund_reason} data-testid={`buyer-refund-reason-text-${b.order_id}`}>
                              «{b.refund_reason}»
                            </p>
                          )}
                          {b.refund_error && b.status !== "refunded" && (
                            <p className="text-xs text-[hsl(var(--warning))] mt-1 inline-flex items-center gap-1" data-testid={`buyer-refund-error-${b.order_id}`}>
                              <AlertTriangle className="w-3 h-3" />{t("Siste refusjon feilet")}
                            </p>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{formatDateTime(b.created_at)}</TableCell>
                        <TableCell>
                          {b.payment === "vipps" && (
                            <div className="flex items-center justify-end gap-1">
                              <Button variant="ghost" size="sm" onClick={() => openPaymentLog(b.order_id)} data-testid={`buyer-payment-log-button-${b.order_id}`}>
                                <History className="w-3.5 h-3.5 mr-1" />
                                {t("Logg")}
                              </Button>
                              {b.can_refund && (
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button variant="outline" size="sm" disabled={refundingId === b.order_id} data-testid={`buyer-refund-button-${b.order_id}`}>
                                      {refundingId === b.order_id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Undo2 className="w-3.5 h-3.5 mr-1" />}
                                      {t("Refunder")}
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent data-testid={`buyer-refund-dialog-${b.order_id}`}>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>{t("Refundere hele bestillingen?")}</AlertDialogTitle>
                                      <AlertDialogDescription asChild>
                                        <div className="space-y-2 text-sm text-muted-foreground">
                                          <p>
                                            {t("{amount} kr refunderes fra din Vipps-konto til {name} ({n} billetter). Beløpet trekkes fra oppgjøret ditt hos Vipps.", { amount: b.total_amount, name: b.name || b.email, n: b.quantity })}
                                          </p>
                                          <p>{t("Billettene blir ugyldige med en gang, plassene blir ledige igjen (ventelisten varsles), og kjøperen får e-post om refusjonen. Dette kan ikke angres.")}</p>
                                        </div>
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <div className="space-y-1.5">
                                      <Label htmlFor={`refund-reason-${b.order_id}`} className="text-sm">
                                        {t("Begrunnelse til kjøperen (valgfritt)")}
                                      </Label>
                                      <Textarea
                                        id={`refund-reason-${b.order_id}`}
                                        value={refundReason}
                                        onChange={(e) => setRefundReason(e.target.value.slice(0, 300))}
                                        placeholder={t("F.eks. «Konserten er avlyst» eller «Dobbeltbestilling»")}
                                        rows={2}
                                        maxLength={300}
                                        data-testid={`buyer-refund-reason-${b.order_id}`}
                                      />
                                      <p className="text-xs text-muted-foreground">{t("Vises i refusjonskvitteringen og i e-posten til kjøperen, med dine egne ord.")} {refundReason.length}/300</p>
                                    </div>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel onClick={() => setRefundReason("")} data-testid={`buyer-refund-cancel-${b.order_id}`}>{t("Avbryt")}</AlertDialogCancel>
                                      <AlertDialogAction onClick={() => refundOrder(b.order_id)} data-testid={`buyer-refund-confirm-${b.order_id}`}>
                                        {t("Ja, refunder {amount} kr", { amount: b.total_amount })}
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              )}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {buyersData.buyers.some((b) => b.status === "refunded") && (
                  <p className="px-4 py-3 text-xs text-muted-foreground border-t border-border" data-testid="buyers-refund-note">
                    {t("Refunderte bestillinger teller ikke i salgsoversikten eller fakturagrunnlaget. Billettene deres avvises ved innsjekk.")}
                  </p>
                )}
                {buyersData.buyers.some((b) => b.gdpr_deleted || b.gdpr_delete_requested) && (
                  <p className="px-4 py-3 text-xs text-muted-foreground border-t border-border" data-testid="buyers-gdpr-note">
                    {t("Rader merket med GDPR er anonymisert (eller blir det etter arrangementet) fordi kjøperen har bedt om sletting av sine data. Antall billetter beholdes for statistikk, og billettene er fortsatt gyldige.")}
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="p-0">
            <div className="p-4 border-b border-border">
              <div className="relative max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t("Søk på navn, e-post eller kode...")}
                  className="pl-9"
                  data-testid="attendees-search-input"
                />
              </div>
            </div>
            {tickets.length === 0 ? (
              <div className="p-12 text-center" data-testid="attendees-empty-state">
                <Users className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                <h3 className="font-heading font-semibold text-lg">{search ? t("Ingen treff") : t("Ingen billetter ennå")}</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {search ? t("Prøv et annet søk.") : t("Når noen henter billetter, dukker de opp her.")}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table data-testid="attendees-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("Kode")}</TableHead>
                      <TableHead>{t("Navn")}</TableHead>
                      <TableHead>{t("E-post")}</TableHead>
                      <TableHead>{t("Billettype")}</TableHead>
                      <TableHead>{t("Status")}</TableHead>
                      <TableHead className="w-24"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tickets.map((tk) => (
                      <TableRow key={tk.id} data-testid={`attendee-row-${tk.code}`}>
                        <TableCell className="font-mono font-medium text-sm">{tk.code}</TableCell>
                        <TableCell className="text-sm">{tk.holder_name}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{tk.holder_email}</TableCell>
                        <TableCell className="text-sm">
                          <span className="inline-flex items-center gap-2 flex-wrap">
                            {tk.ticket_type || "–"}
                            {tk.payment === "door" && (
                              <Badge className="bg-[hsl(var(--warning))]/20 text-foreground border border-[hsl(var(--warning))]/60 hover:bg-[hsl(var(--warning))]/20 font-normal whitespace-nowrap" data-testid={`attendee-door-badge-${tk.code}`}>
                                <HandCoins className="w-3 h-3 mr-1" />{t("Betal ved inngangen")}{tk.unit_price ? ` · ${tk.unit_price} kr` : ""}
                              </Badge>
                            )}
                          </span>
                        </TableCell>
                        <TableCell>
                          {tk.checked_in_at ? (
                            <Badge className="bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] hover:bg-[hsl(var(--success))]">
                              {t("Innsjekket {time}", { time: formatTime(tk.checked_in_at) })}
                            </Badge>
                          ) : (
                            <Badge variant="secondary">{t("Ikke innsjekket")}</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {tk.checked_in_at && (
                            <Button variant="ghost" size="sm" onClick={() => undoCheckin(tk.code)} data-testid={`attendee-undo-${tk.code}`}>
                              <RotateCcw className="w-3.5 h-3.5 mr-1" />
                              {t("Angre")}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Betalingslogg for ei Vipps-bestilling: Vipps sin hendingslogg + varsel (webhooks) Right in har teke imot */}
        <Dialog open={!!logOrderId} onOpenChange={(open) => { if (!open) { setLogOrderId(null); setLogData(null); } }}>
          <DialogContent className="max-w-2xl" data-testid="payment-log-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><History className="w-5 h-5" />{t("Betalingslogg (Vipps)")}</DialogTitle>
              <DialogDescription>
                {t("Hendelsene for denne bestillingen slik Vipps har registrert dem, og varslene Right in har mottatt.")}
              </DialogDescription>
            </DialogHeader>
            {logLoading || !logData ? (
              <div className="space-y-3" data-testid="payment-log-loading">
                <Skeleton className="h-6 w-2/3" />
                <Skeleton className="h-24 rounded-xl" />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm" data-testid="payment-log-summary">
                  <div className="rounded-xl bg-secondary/60 p-3">
                    <p className="text-xs text-muted-foreground">{t("Status")}</p>
                    <p className="font-medium" data-testid="payment-log-order-status">{t(ORDER_STATUS_LABELS[logData.order?.status] || logData.order?.status || "–")}</p>
                  </div>
                  <div className="rounded-xl bg-secondary/60 p-3">
                    <p className="text-xs text-muted-foreground">{t("Beløp")}</p>
                    <p className="font-medium tabular-nums">{logData.order?.total_amount ?? "–"} kr</p>
                  </div>
                  <div className="rounded-xl bg-secondary/60 p-3">
                    <p className="text-xs text-muted-foreground">{t("Belastet hos Vipps")}</p>
                    <p className="font-medium tabular-nums">{logData.vipps_payment ? kr(logData.vipps_payment.captured_ore) : "–"}</p>
                  </div>
                  <div className="rounded-xl bg-secondary/60 p-3">
                    <p className="text-xs text-muted-foreground">{t("Refundert hos Vipps")}</p>
                    <p className="font-medium tabular-nums">{logData.vipps_payment ? kr(logData.vipps_payment.refunded_ore) : "–"}</p>
                  </div>
                </div>
                {logData.vipps_payment?.psp_reference && (
                  <p className="text-xs text-muted-foreground" data-testid="payment-log-psp">
                    {t("Vipps-referanse")}: <span className="font-mono">{logData.vipps_payment.psp_reference}</span> · {t("Ordre-ID")}: <span className="font-mono">{logData.order?.id}</span>
                  </p>
                )}
                {logData.vipps_error && (
                  <div className="rounded-xl border border-[hsl(var(--warning))]/60 bg-[hsl(var(--warning))]/10 p-3 text-sm flex items-start gap-2" data-testid="payment-log-vipps-error">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>{t("Kunne ikke hente loggen fra Vipps akkurat nå")}: {logData.vipps_error}</span>
                  </div>
                )}
                {logData.order?.refund_reason && (
                  <p className="text-sm" data-testid="payment-log-refund-reason">
                    <span className="text-muted-foreground">{t("Begrunnelse til kjøperen")}:</span> <span className="italic">«{logData.order.refund_reason}»</span>
                  </p>
                )}
                {logData.order?.refund_error && (
                  <div className="rounded-xl border border-[hsl(var(--warning))]/60 bg-[hsl(var(--warning))]/10 p-3 text-sm" data-testid="payment-log-refund-error">
                    <span className="font-medium">{t("Siste refusjonsforsøk feilet")}:</span> {logData.order.refund_error.message}
                    {logData.order.refund_error.trace_id ? <span className="font-mono text-xs"> (traceId {logData.order.refund_error.trace_id})</span> : null}
                  </div>
                )}
                {paymentLogRows.length === 0 ? (
                  <p className="text-sm text-muted-foreground" data-testid="payment-log-empty">{t("Ingen hendelser registrert ennå.")}</p>
                ) : (
                  <div className="overflow-x-auto rounded-xl border border-border">
                    <Table data-testid="payment-log-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t("Tidspunkt")}</TableHead>
                          <TableHead>{t("Hendelse")}</TableHead>
                          <TableHead className="text-right">{t("Beløp")}</TableHead>
                          <TableHead>{t("Kilde")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paymentLogRows.map((row) => (
                          <TableRow key={row.key} data-testid={`payment-log-row-${row.source}-${row.name}`}>
                            <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{row.time ? formatDateTime(row.time) : "–"}</TableCell>
                            <TableCell className="text-sm">
                              <span className="inline-flex items-center gap-1.5">
                                {row.success ? <CheckCircle2 className="w-3.5 h-3.5 text-[hsl(var(--success))]" /> : <AlertTriangle className="w-3.5 h-3.5 text-[hsl(var(--warning))]" />}
                                {t(VIPPS_EVENT_LABELS[row.name] || row.name)}
                              </span>
                              {row.detail && <span className="ml-1.5 text-xs text-muted-foreground">({row.detail})</span>}
                            </TableCell>
                            <TableCell className="text-sm text-right tabular-nums">{kr(row.amount)}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{row.source === "vipps" ? t("Vipps hendelseslogg") : t("Varsel mottatt")}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {waitlist.count > 0 && (
          <Card className="border-border" data-testid="waitlist-card">
            <CardContent className="p-0">
              <div className="p-4 border-b border-border flex items-center gap-2">
                <Hourglass className="w-4 h-4 text-muted-foreground" />
                <h2 className="font-heading font-semibold">{t("Venteliste ({n})", { n: waitlist.count })}</h2>
                <span className="text-xs text-muted-foreground ml-auto">{t("Varsles automatisk på e-post når det blir ledige plasser")}</span>
              </div>
              <div className="overflow-x-auto">
                <Table data-testid="waitlist-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("Navn")}</TableHead>
                      <TableHead>{t("E-post")}</TableHead>
                      <TableHead>{t("Påmeldt")}</TableHead>
                      <TableHead>{t("Status")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {waitlist.entries.map((w) => (
                      <TableRow key={w.id} data-testid={`waitlist-row-${w.id}`}>
                        <TableCell className="text-sm">{w.name}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{w.email}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{formatDateTime(w.created_at)}</TableCell>
                        <TableCell>
                          {w.notified_at ? (
                            <Badge className="bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] hover:bg-[hsl(var(--success))]">{t("Varslet")}</Badge>
                          ) : (
                            <Badge variant="secondary">{t("Venter")}</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
